import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  createAssignment,
  getAssignment,
  updateAssignment,
  getLesson
} from '../../api/educentialApi';
import './AssignmentBuilder.css';

function AssignmentBuilder() {
  const { lessonId, assignmentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lesson, setLesson] = useState(null);
  const [notification, setNotification] = useState(null);

  const [assignmentData, setAssignmentData] = useState({
    title: '',
    description: '',
    instructions: '',
    dueDate: '',
    maxScore: 100,
    allowLateSubmission: true,
    latePenalty: 10, // Percentage per day
    requireFile: false,
    requireText: true,
    allowedFileTypes: 'pdf,doc,docx,txt,zip',
    maxFileSize: 10, // MB
    rubric: ''
  });

  useEffect(() => {
    loadData();
  }, [lessonId, assignmentId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load lesson details
      if (lessonId) {
        const lessonResponse = await getLesson(lessonId);
        if (lessonResponse.success) {
          setLesson(lessonResponse.data);
        }
      }

      // Load existing assignment if editing
      if (assignmentId) {
        const response = await getAssignment(assignmentId);
        if (response.success) {
          const assignment = response.data;
          setAssignmentData({
            title: assignment.title || '',
            description: assignment.description || '',
            instructions: assignment.instructions || '',
            dueDate: assignment.dueDate ? new Date(assignment.dueDate).toISOString().slice(0, 16) : '',
            maxScore: assignment.maxScore || 100,
            allowLateSubmission: assignment.allowLateSubmission !== undefined ? assignment.allowLateSubmission : true,
            latePenalty: assignment.latePenalty || 10,
            requireFile: assignment.requireFile || false,
            requireText: assignment.requireText !== undefined ? assignment.requireText : true,
            allowedFileTypes: assignment.allowedFileTypes || 'pdf,doc,docx,txt,zip',
            maxFileSize: assignment.maxFileSize || 10,
            rubric: assignment.rubric || ''
          });
          if (assignment.Lesson) {
            setLesson(assignment.Lesson);
          }
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showNotification('Failed to load assignment data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setAssignmentData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleSave = async () => {
    try {
      // Validation
      if (!assignmentData.title.trim()) {
        showNotification('Please enter an assignment title', 'error');
        return;
      }

      if (!assignmentData.description.trim()) {
        showNotification('Please enter a description', 'error');
        return;
      }

      if (assignmentData.maxScore <= 0) {
        showNotification('Max score must be greater than 0', 'error');
        return;
      }

      setSaving(true);

      const payload = {
        ...assignmentData,
        dueDate: assignmentData.dueDate || null,
        maxScore: parseInt(assignmentData.maxScore),
        latePenalty: assignmentData.allowLateSubmission ? parseInt(assignmentData.latePenalty) : null,
        maxFileSize: parseInt(assignmentData.maxFileSize)
      };

      let response;
      if (assignmentId) {
        response = await updateAssignment(assignmentId, payload);
      } else {
        response = await createAssignment(lessonId, payload);
      }

      if (response.success) {
        showNotification(
          assignmentId ? 'Assignment updated successfully!' : 'Assignment created successfully!',
          'success'
        );
        setTimeout(() => {
          navigate(-1); // Go back to previous page
        }, 1500);
      } else {
        showNotification(response.error || 'Failed to save assignment', 'error');
      }
    } catch (error) {
      console.error('Error saving assignment:', error);
      showNotification(error.response?.data?.error || 'Failed to save assignment', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="assignment-builder">
        <div className="loading-spinner">Loading assignment...</div>
      </div>
    );
  }

  return (
    <div className="assignment-builder">
      {notification && (
        <div className={`notification notification-${notification.type}`}>
          {notification.message}
        </div>
      )}

      <div className="assignment-builder-header">
        <div>
          <h1>{assignmentId ? 'Edit Assignment' : 'Create Assignment'}</h1>
          {lesson && (
            <p className="lesson-info">
              Lesson: {lesson.title}
            </p>
          )}
        </div>
        <div className="header-actions">
          <button onClick={handleCancel} className="btn-secondary" disabled={saving}>
            Cancel
          </button>
          <button onClick={handleSave} className="btn-primary" disabled={saving}>
            {saving ? 'Saving...' : assignmentId ? 'Update Assignment' : 'Create Assignment'}
          </button>
        </div>
      </div>

      <div className="assignment-builder-content">
        {/* Basic Information */}
        <div className="form-section">
          <h2>Basic Information</h2>

          <div className="form-group">
            <label>Assignment Title *</label>
            <input
              type="text"
              value={assignmentData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Enter assignment title"
              className="form-control"
            />
          </div>

          <div className="form-group">
            <label>Description *</label>
            <textarea
              value={assignmentData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Provide a brief description of the assignment"
              className="form-control"
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>Instructions</label>
            <textarea
              value={assignmentData.instructions}
              onChange={(e) => handleChange('instructions', e.target.value)}
              placeholder="Detailed instructions for students (optional)"
              className="form-control"
              rows="5"
            />
          </div>
        </div>

        {/* Assignment Settings */}
        <div className="form-section">
          <h2>Assignment Settings</h2>

          <div className="form-row">
            <div className="form-group">
              <label>Due Date</label>
              <input
                type="datetime-local"
                value={assignmentData.dueDate}
                onChange={(e) => handleChange('dueDate', e.target.value)}
                className="form-control"
              />
              <small>Leave empty for no deadline</small>
            </div>

            <div className="form-group">
              <label>Maximum Score *</label>
              <input
                type="number"
                value={assignmentData.maxScore}
                onChange={(e) => handleChange('maxScore', e.target.value)}
                min="1"
                className="form-control"
              />
            </div>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={assignmentData.allowLateSubmission}
                onChange={(e) => handleChange('allowLateSubmission', e.target.checked)}
              />
              <span>Allow late submissions</span>
            </label>
          </div>

          {assignmentData.allowLateSubmission && (
            <div className="form-group">
              <label>Late Penalty (% per day)</label>
              <input
                type="number"
                value={assignmentData.latePenalty}
                onChange={(e) => handleChange('latePenalty', e.target.value)}
                min="0"
                max="100"
                className="form-control"
              />
              <small>Percentage of points deducted per day late (0-100)</small>
            </div>
          )}
        </div>

        {/* Submission Requirements */}
        <div className="form-section">
          <h2>Submission Requirements</h2>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={assignmentData.requireText}
                onChange={(e) => handleChange('requireText', e.target.checked)}
              />
              <span>Require text response</span>
            </label>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={assignmentData.requireFile}
                onChange={(e) => handleChange('requireFile', e.target.checked)}
              />
              <span>Require file upload</span>
            </label>
          </div>

          {assignmentData.requireFile && (
            <>
              <div className="form-group">
                <label>Allowed File Types</label>
                <input
                  type="text"
                  value={assignmentData.allowedFileTypes}
                  onChange={(e) => handleChange('allowedFileTypes', e.target.value)}
                  placeholder="pdf,doc,docx,txt,zip"
                  className="form-control"
                />
                <small>Comma-separated file extensions (e.g., pdf,doc,docx)</small>
              </div>

              <div className="form-group">
                <label>Maximum File Size (MB)</label>
                <input
                  type="number"
                  value={assignmentData.maxFileSize}
                  onChange={(e) => handleChange('maxFileSize', e.target.value)}
                  min="1"
                  max="50"
                  className="form-control"
                />
              </div>
            </>
          )}
        </div>

        {/* Grading Rubric */}
        <div className="form-section">
          <h2>Grading Rubric (Optional)</h2>

          <div className="form-group">
            <label>Rubric / Grading Criteria</label>
            <textarea
              value={assignmentData.rubric}
              onChange={(e) => handleChange('rubric', e.target.value)}
              placeholder="Define how this assignment will be graded (optional)"
              className="form-control"
              rows="6"
            />
            <small>Provide clear criteria for grading to help students understand expectations</small>
          </div>
        </div>
      </div>

      <div className="assignment-builder-footer">
        <button onClick={handleCancel} className="btn-secondary" disabled={saving}>
          Cancel
        </button>
        <button onClick={handleSave} className="btn-primary" disabled={saving}>
          {saving ? 'Saving...' : assignmentId ? 'Update Assignment' : 'Create Assignment'}
        </button>
      </div>
    </div>
  );
}

export default AssignmentBuilder;
